import type { CollisionWorld } from './collision.js';
import type { Vec2 } from './math.js';

const SQRT2 = Math.SQRT2;

/** Binary min-heap keyed by f-score, storing cell indices. */
class MinHeap {
  private readonly items: number[] = [];
  private readonly keys: number[] = [];
  get size(): number {
    return this.items.length;
  }
  push(item: number, key: number): void {
    const items = this.items;
    const keys = this.keys;
    let i = items.length;
    items.push(item);
    keys.push(key);
    while (i > 0) {
      const p = (i - 1) >> 1;
      if ((keys[p] as number) <= key) break;
      items[i] = items[p] as number;
      keys[i] = keys[p] as number;
      i = p;
    }
    items[i] = item;
    keys[i] = key;
  }
  pop(): number {
    const items = this.items;
    const keys = this.keys;
    const top = items[0] as number;
    const lastItem = items.pop() as number;
    const lastKey = keys.pop() as number;
    const n = items.length;
    if (n > 0) {
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        if (l >= n) break;
        const r = l + 1;
        const c = r < n && (keys[r] as number) < (keys[l] as number) ? r : l;
        if ((keys[c] as number) >= lastKey) break;
        items[i] = items[c] as number;
        keys[i] = keys[c] as number;
        i = c;
      }
      items[i] = lastItem;
      keys[i] = lastKey;
    }
    return top;
  }
}

/**
 * Uniform walkability grid rasterised from the collision world, with A* search and
 * line-of-sight path smoothing. Used by the server for monster navigation.
 */
export class NavGrid {
  readonly cellSize: number;
  readonly cols: number;
  readonly rows: number;
  readonly originX: number;
  readonly originZ: number;
  private readonly blocked: Uint8Array;
  private readonly g: Float32Array;
  private readonly parent: Int32Array;
  private readonly visit: Uint32Array;
  private readonly closed: Uint32Array;
  private searchId = 0;

  constructor(world: CollisionWorld, agentRadius: number, cellSize = 1) {
    this.cellSize = cellSize;
    const b = world.bounds;
    this.originX = b.minX;
    this.originZ = b.minZ;
    this.cols = Math.ceil((b.maxX - b.minX) / cellSize);
    this.rows = Math.ceil((b.maxZ - b.minZ) / cellSize);
    const n = this.cols * this.rows;
    this.blocked = new Uint8Array(n);
    this.g = new Float32Array(n);
    this.parent = new Int32Array(n);
    this.visit = new Uint32Array(n);
    this.closed = new Uint32Array(n);
    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const c = this.cellCenter(col, row);
        this.blocked[row * this.cols + col] = world.overlaps(c.x, c.z, agentRadius) ? 1 : 0;
      }
    }
  }

  cellCenter(col: number, row: number): Vec2 {
    return {
      x: this.originX + (col + 0.5) * this.cellSize,
      z: this.originZ + (row + 0.5) * this.cellSize,
    };
  }

  colOf(x: number): number {
    return Math.floor((x - this.originX) / this.cellSize);
  }

  rowOf(z: number): number {
    return Math.floor((z - this.originZ) / this.cellSize);
  }

  isWalkableCell(col: number, row: number): boolean {
    if (col < 0 || row < 0 || col >= this.cols || row >= this.rows) return false;
    return this.blocked[row * this.cols + col] === 0;
  }

  isWalkable(x: number, z: number): boolean {
    return this.isWalkableCell(this.colOf(x), this.rowOf(z));
  }

  /** Nearest walkable cell to (col,row) within `maxRing` rings, or null. */
  private nearestWalkable(col: number, row: number, maxRing = 6): [number, number] | null {
    if (this.isWalkableCell(col, row)) return [col, row];
    for (let ring = 1; ring <= maxRing; ring++) {
      for (let dc = -ring; dc <= ring; dc++) {
        for (let dr = -ring; dr <= ring; dr++) {
          if (Math.max(Math.abs(dc), Math.abs(dr)) !== ring) continue;
          if (this.isWalkableCell(col + dc, row + dr)) return [col + dc, row + dr];
        }
      }
    }
    return null;
  }

  /** Grid line-of-sight (supercover DDA): every cell crossed by the segment must be walkable. */
  lineWalkable(ax: number, az: number, bx: number, bz: number): boolean {
    const dx = bx - ax;
    const dz = bz - az;
    const len = Math.sqrt(dx * dx + dz * dz);
    const steps = Math.max(1, Math.ceil(len / (this.cellSize * 0.35)));
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      if (!this.isWalkable(ax + dx * t, az + dz * t)) return false;
    }
    return true;
  }

  /**
   * A* from (sx,sz) to (tx,tz). Returns smoothed waypoints (excluding the start, including the goal),
   * or null if no path is found within `maxExpanded` node expansions.
   */
  findPath(sx: number, sz: number, tx: number, tz: number, maxExpanded = 6000): Vec2[] | null {
    const start = this.nearestWalkable(this.colOf(sx), this.rowOf(sz));
    const goal = this.nearestWalkable(this.colOf(tx), this.rowOf(tz));
    if (!start || !goal) return null;
    const cols = this.cols;
    const startIdx = start[1] * cols + start[0];
    const goalIdx = goal[1] * cols + goal[0];
    if (startIdx === goalIdx) return [{ x: tx, z: tz }];

    this.searchId = (this.searchId + 1) >>> 0;
    if (this.searchId === 0) {
      this.visit.fill(0);
      this.closed.fill(0);
      this.searchId = 1;
    }
    const sid = this.searchId;
    const gc = goal[0];
    const gr = goal[1];
    const h = (c: number, r: number): number => {
      const dx = Math.abs(c - gc);
      const dy = Math.abs(r - gr);
      return (dx + dy + (SQRT2 - 2) * Math.min(dx, dy)) * this.cellSize;
    };
    const open = new MinHeap();
    this.g[startIdx] = 0;
    this.parent[startIdx] = -1;
    this.visit[startIdx] = sid;
    open.push(startIdx, h(start[0], start[1]));
    let expanded = 0;
    let found = false;
    while (open.size > 0) {
      const cur = open.pop();
      if (this.closed[cur] === sid) continue;
      this.closed[cur] = sid;
      if (cur === goalIdx) {
        found = true;
        break;
      }
      if (++expanded > maxExpanded) break;
      const cc = cur % cols;
      const cr = (cur - cc) / cols;
      const gCur = this.g[cur] as number;
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          if (dc === 0 && dr === 0) continue;
          const nc = cc + dc;
          const nr = cr + dr;
          if (!this.isWalkableCell(nc, nr)) continue;
          // No corner cutting through blocked diagonals.
          if (
            dc !== 0 &&
            dr !== 0 &&
            (!this.isWalkableCell(cc + dc, cr) || !this.isWalkableCell(cc, cr + dr))
          ) {
            continue;
          }
          const ni = nr * cols + nc;
          if (this.closed[ni] === sid) continue;
          const step = (dc !== 0 && dr !== 0 ? SQRT2 : 1) * this.cellSize;
          const ng = gCur + step;
          if (this.visit[ni] !== sid || ng < (this.g[ni] as number)) {
            this.visit[ni] = sid;
            this.g[ni] = ng;
            this.parent[ni] = cur;
            open.push(ni, ng + h(nc, nr));
          }
        }
      }
    }
    if (!found) return null;

    const cells: Vec2[] = [];
    for (let i = goalIdx; i !== -1; i = this.parent[i] as number) {
      const c = i % cols;
      cells.push(this.cellCenter(c, (i - c) / cols));
    }
    cells.reverse();
    // Replace the last cell centre with the exact target when it is walkable.
    if (this.isWalkable(tx, tz)) cells[cells.length - 1] = { x: tx, z: tz };

    // String-pulling: keep only the waypoints needed to preserve line of sight.
    const smoothed: Vec2[] = [];
    let anchor: Vec2 = { x: sx, z: sz };
    let i = 0;
    while (i < cells.length) {
      let j = cells.length - 1;
      while (j > i && !this.lineWalkable(anchor.x, anchor.z, (cells[j] as Vec2).x, (cells[j] as Vec2).z)) j--;
      const wp = cells[j] as Vec2;
      smoothed.push(wp);
      anchor = wp;
      i = j + 1;
    }
    return smoothed;
  }
}
